/**
 * Postal code validation patterns by country code
 */
const POSTAL_CODE_PATTERNS = {
  GB: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
  AU: /^\d{4}$/,
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  FR: /^\d{5}$/,
  IT: /^\d{5}$/,
  ES: /^\d{5}$/,
  NL: /^\d{4}\s?[A-Z]{2}$/i,
  BE: /^\d{4}$/,
  CH: /^\d{4}$/,
  SE: /^\d{5}$/,
  NO: /^\d{4}$/,
  DK: /^\d{4}$/,
  FI: /^\d{5}$/,
  PL: /^\d{2}-\d{3}$/,
  CZ: /^\d{5}$/,
  IE: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i,
  PT: /^\d{4}-\d{3}$/,
  GR: /^\d{5}$/,
  JP: /^\d{3}-\d{4}$/,
  SG: /^\d{6}$/,
  HK: /^$/, // Hong Kong doesn't use postal codes
  TW: /^\d{3}(\d{2})?$/,
  KR: /^\d{5}$/,
  IN: /^\d{6}$/,
  MX: /^\d{5}$/,
  BR: /^\d{5}-\d{3}$/,
  AR: /^[A-Z]\d{4}[A-Z]{3}$/i,
  CL: /^\d{7}$/,
  NZ: /^\d{4}$/,
  ZA: /^\d{4}$/,
  AE: /^$/, // UAE doesn't consistently use postal codes
  SA: /^\d{5}(-\d{4})?$/,
  TR: /^\d{5}$/,
  RU: /^\d{6}$/,
  CN: /^\d{6}$/,
  TH: /^\d{5}$/,
  MY: /^\d{5}$/,
  ID: /^\d{5}$/,
  PH: /^\d{4}$/,
  VN: /^\d{6}$/,
};

/**
 * Validate a postal code for a specific country
 * @param {string} postalCode - The postal code to validate
 * @param {string} countryCode - The ISO country code (e.g., 'GB', 'US')
 * @returns {boolean} - True if valid, false otherwise
 */
export function validatePostalCode(postalCode, countryCode) {
  if (!postalCode || !countryCode) {
    return false;
  }

  const pattern = POSTAL_CODE_PATTERNS[countryCode.toUpperCase()];

  if (!pattern) {
    // For countries without defined patterns, accept any non-empty value
    return postalCode.trim().length > 0;
  }

  // Empty pattern means country doesn't use postal codes
  if (pattern.source === "^$") {
    return false;
  }

  return pattern.test(postalCode.trim());
}

/**
 * Parse latitude and longitude from Google Maps URL
 * @param {string} url - Google Maps URL containing coordinates
 * @returns {{lat: number, lng: number} | null} - Coordinates or null if not found
 */
export function parseCoordinatesFromUrl(url) {
  if (!url) return null;

  try {
    // Try to match various Google Maps URL formats:
    // 1. https://maps.google.com/?q=51.5074,-0.1278
    // 2. https://www.google.com/maps?q=51.5074,-0.1278
    // 3. https://www.google.com/maps/place/@51.5074,-0.1278,17z
    // 4. https://www.google.com/maps/@51.5074,-0.1278,17z

    // Pattern 1: ?q=lat,lng
    let match = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return {
        lat: parseFloat(match[1]),
        lng: parseFloat(match[2]),
      };
    }

    // Pattern 2: /@lat,lng
    match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return {
        lat: parseFloat(match[1]),
        lng: parseFloat(match[2]),
      };
    }

    // Pattern 3: /place/Name/@lat,lng
    match = url.match(/\/place\/[^/]+\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return {
        lat: parseFloat(match[1]),
        lng: parseFloat(match[2]),
      };
    }

    return null;
  } catch (error) {
    console.error("Error parsing coordinates from URL:", error);
    return null;
  }
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @returns {number} - Distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Angle in degrees
 * @returns {number} - Angle in radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Format distance for display
 * @param {number} distance - Distance in kilometers
 * @param {string} locale - Locale for formatting (e.g., 'en-gb')
 * @returns {string} - Formatted distance string
 */
export function formatDistance(distance, locale = "en-gb") {
  // Use miles for US, UK, and other imperial countries
  const useImperial = ["en-us", "en-gb", "en-au"].includes(locale.toLowerCase());

  if (useImperial) {
    const miles = distance * 0.621371;
    return miles < 0.1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;
  }

  return distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`;
}
